import { z } from 'zod';

/** Normalisierte Profil-URL: https, Host `www.linkedin.com`, ohne Schlussstrich, Query, Fragment. */
export const PROFILE_URL_PATTERN =
  /^https:\/\/www\.linkedin\.com\/in\/(?!(?:\.|%2[eE]){1,2}$)[^/\\?#\s]+$/;
const MEMBER_URN_PATTERN = /^urn:li:person:[A-Za-z0-9_-]+$/;

/** Obergrenze einer E-Mail-Adresse (RFC 5321, Pfad ≤ 254). */
export const EMAIL_ADDRESS_MAX_LENGTH = 254;
/** RFC-5321-Grobmuster: ein Klammeraffe, Leerraum verboten, Endung `[a-z0-9-]+` (Plan Welle 2 A, VB-2 a). */
const EMAIL_ADDRESS_PATTERN = /^[^\s@]+@[^\s@]+\.[a-z0-9-]+$/;

/**
 * Odoo-Partner (Plan Welle 2 A, VB-2 a): `<origin>#<id>`. `origin` = `https://`-Ursprung der Instanz in Kleinbuchstaben ohne
 * Pfad, Query, Zugangsdaten und Schlussstrich (Host aus Bezeichnern mit Punkten, optional `:port`); `id` = positive Ganzzahl
 * ohne führende Null (höchstens 15 Ziffern, damit sie eine sichere Ganzzahl ist).
 */
const ODOO_PARTNER_PATTERN =
  /^https:\/\/[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*(?::[1-9]\d{0,4})?#[1-9]\d{0,14}$/;

/**
 * Ursprung eindeutig: der Standardport 443 wird weggelassen (`https://erp.example#42` und `…:443#42` wären zwei Schlüssel
 * für dieselbe Instanz), ein Port ist höchstens 65535 und hat keine führende Null.
 */
function isCanonicalOdooPartner(value: string): boolean {
  if (!ODOO_PARTNER_PATTERN.test(value)) {
    return false;
  }
  const port = /^https:\/\/[^#:]+:(\d+)#/.exec(value)?.[1];
  if (port !== undefined && (port === '443' || Number(port) > 65535)) {
    return false;
  }
  // IPv4-Aliasse (`127.1`, `0x7f.1`, Dezimalzahl) hätten mehrere Schreibweisen für denselben Ursprung: nur die
  // Schreibweise, die der URL-Parser selbst zurückgibt, ist kanonisch
  const origin = value.slice(0, value.indexOf('#'));
  try {
    return new URL(origin).origin === origin;
  } catch {
    return false;
  }
}

/**
 * E-Mail-Adresse in der Form der Identität und der Rohdaten: klein geschrieben, höchstens 254 Zeichen, genau ein
 * Klammeraffe, Leerraum verboten. Es gibt keine stille Normalisierung: der Konnektor schreibt klein, das Schema prüft.
 */
export const EmailAddressSchema = z
  .string()
  .max(EMAIL_ADDRESS_MAX_LENGTH)
  .refine((address) => EMAIL_ADDRESS_PATTERN.test(address) && address === address.toLowerCase(), {
    message: 'keine kleingeschriebene E-Mail-Adresse (ein @, Domäne mit Punkt, kein Leerraum)',
  });

const LinkedInIdentitySchema = z
  .strictObject({
    namespace: z.literal('linkedin'),
    kind: z.enum(['profile_url', 'member_urn']),
    value: z.string(),
  })
  .refine(
    (identity) =>
      (identity.kind === 'profile_url' ? PROFILE_URL_PATTERN : MEMBER_URN_PATTERN).test(
        identity.value,
      ),
    { message: 'value passt nicht zum Muster der Identitätsart', path: ['value'] },
  );

const EmailIdentitySchema = z.strictObject({
  namespace: z.literal('email'),
  kind: z.literal('email'),
  value: EmailAddressSchema,
});

const OdooIdentitySchema = z
  .strictObject({
    namespace: z.literal('odoo'),
    kind: z.literal('odoo_partner'),
    value: z.string(),
  })
  .refine((identity) => isCanonicalOdooPartner(identity.value), {
    message: 'value passt nicht zum Muster der Identitätsart',
    path: ['value'],
  });

/**
 * Identität in einem Namensraum; `value` ist je Art normalisiert ([A] 3.3.1, ohne Schlussstrich). Formen:
 * `linkedin` (`profile_url`, `member_urn`), `email` (`email`) und `odoo` (`odoo_partner`, VB-2 a).
 */
export const IdentityRefSchema = z.discriminatedUnion('namespace', [
  LinkedInIdentitySchema,
  EmailIdentitySchema,
  OdooIdentitySchema,
]);
export type IdentityRef = z.infer<typeof IdentityRefSchema>;

export const AuthorRefSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('self') }),
  z.strictObject({ kind: z.literal('identity'), identity: IdentityRefSchema }),
  z.strictObject({ kind: z.literal('unknown') }),
]);
export type AuthorRef = z.infer<typeof AuthorRefSchema>;

export const ParticipantRefSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('self') }),
  z.strictObject({
    kind: z.literal('identity'),
    identity: IdentityRefSchema,
    displayName: z.string().exactOptional(),
  }),
  z.strictObject({ kind: z.literal('name'), displayName: z.string() }),
]);
export type ParticipantRef = z.infer<typeof ParticipantRefSchema>;
